import React, { useState, useRef, useEffect } from 'react';
import { NamedLink } from '../../components';
import { createSlug } from '../../util/urlHelpers';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import type { FeaturedListing } from './types';
import { formatPublicLocation } from '../../util/listingLocationDisplay';

interface FeaturedCarouselProps {
  listings: FeaturedListing[];
}

const FeaturedCarousel = ({ listings }: FeaturedCarouselProps) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const featured = listings.slice(0, 8);

  useEffect(() => {
    if (featured.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % featured.length);
    }, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [featured.length]);

  const go = (dir: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCurrent(prev => (prev + dir + featured.length) % featured.length);
  };

  if (!featured.length) return null;

  const item = featured[current];
  const { title = '', price, publicData = {} } = item?.attributes || {};
  const firstImage = item?.images?.[0];
  const imgVariants = firstImage?.attributes?.variants || {};
  const imgUrl =
    imgVariants['scaled-xlarge']?.url ||
    imgVariants['listing-card-2x']?.url ||
    imgVariants['listing-card']?.url ||
    '';

  const { amount, currency } = price || {};
  const formattedPrice =
    amount != null
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: 0,
        }).format(amount / 100)
      : null;

  const location = formatPublicLocation(publicData?.location);
  const id = item?.id?.uuid;
  const slug = createSlug(title);

  return (
    <section className="mb-16">
      <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-2 uppercase">
        <span className="bg-accent text-accent-foreground px-2 py-1 rounded text-sm tracking-widest">
          Featured
        </span>
        Hot Listings
      </h2>

      <div className="relative w-full">
        <NamedLink name="ListingPage" params={{ id, slug }}>
          <div className="relative aspect-[21/9] sm:aspect-[24/9] lg:aspect-[32/9] overflow-hidden rounded-xl group cursor-pointer border border-border bg-card shadow-sm block">
            {imgUrl ? (
              <img
                src={imgUrl}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-muted" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-sm">
                  Featured
                </span>
                {location && (
                  <span className="text-white/80 text-sm font-medium flex items-center gap-1">
                    {location}
                  </span>
                )}
              </div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-2 leading-tight">
                {title}
              </h3>
              {formattedPrice && (
                <p className="text-xl md:text-2xl font-bold text-accent">{formattedPrice}</p>
              )}
            </div>
          </div>
        </NamedLink>

        {featured.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/50 backdrop-blur hover:bg-background border-none text-foreground rounded-full p-2 shadow"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => go(1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/50 backdrop-blur hover:bg-background border-none text-foreground rounded-full p-2 shadow"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedCarousel;
